import { useCallback } from "react";

import type { Payjp } from "@/lib/payjp/type";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import * as UseCase from "../../model/use-case";

/**
 * mutation を wrap し、Payjp へクレジットカード情報を送信し、Token を生成する
 */
export const useUpdatePaymentCredit = (
	hotelID: string,
	onCreatePayPayjpToken: () => Promise<
		Payjp.Event.Instance.CreateTokenResult.Success | Payjp.Event.Instance.CreateTokenResult.Error | undefined
	>,
	successCallback: () => void,
) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { onUpdatePaymentCredit } = UseCase.useUpdatePaymentCredit({ hotelID });
	return useCallback(async () => {
		showLoadingScreenCallback();

		// Payjp へクレジットカード情報を送信し、Token を生成する
		const result = await onCreatePayPayjpToken();
		if (result && "error" in result) {
			notifyUserEventInfo({
				variant: "error",
				title: result.error.message,
			});

			hideLoadingScreenCallback();
			return;
		}

		const { errors } = await onUpdatePaymentCredit({ paymentToken: result?.id ?? "" }).finally(
			hideLoadingScreenCallback,
		);

		if (!errors) {
			notifyUserEventInfo({
				title: "お支払い情報を更新しました",
			});
			successCallback();
		}
	}, [
		hideLoadingScreenCallback,
		onCreatePayPayjpToken,
		onUpdatePaymentCredit,
		showLoadingScreenCallback,
		successCallback,
	]);
};
